import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

export const NAV_ITEMS = [
  { to: '/', label: 'Startseite' },
  { to: '/rezepte', label: 'Rezepte' },
  { to: '/wissen', label: 'Wissen' },
  { to: '/felix', label: 'Über Felix' },
  { to: '/magazin', label: 'Magazin' },
  { to: '/kontakt', label: 'Kontakt' },
];

export function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const firstLink = menuRef.current?.querySelector('a');
    firstLink?.focus();
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const linkClass = ({ isActive }) =>
    `inline-flex items-center rounded-sm px-3 py-2 text-base font-semibold transition-colors duration-200 ${
      isActive ? 'text-brand-olive' : 'text-brand-brown hover:text-brand-olive'
    }`;

  return (
    <header className="border-b border-brand-line bg-brand-cream">
      <div className="container-site flex items-center justify-between gap-4 py-4">
        <Link
          to="/"
          className="flex items-center gap-3 rounded-sm"
          aria-label="Felix Genuss – zur Startseite"
          data-testid="header-logo-link"
        >
          <img
            src="/assets/brand/logo-256.webp"
            alt="Felix Genuss Logo – Felix, ein Bolonka Zwetna, im runden Markenzeichen"
            width="56"
            height="56"
            className="h-12 w-12 sm:h-14 sm:w-14"
          />
          <span className="leading-tight">
            <span className="block font-serif text-xl font-bold tracking-tight sm:text-2xl">Felix Genuss</span>
            <span className="block text-[0.65rem] font-semibold uppercase tracking-[0.22em] text-brand-brown-soft sm:text-xs">
              Feinschmecker · seit 2025
            </span>
          </span>
        </Link>

        <nav aria-label="Hauptnavigation" className="hidden lg:block">
          <ul className="flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === '/'}
                  className={linkClass}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <button
          ref={buttonRef}
          type="button"
          className="inline-flex min-h-12 min-w-12 items-center justify-center gap-2 rounded-sm border border-brand-brown/30 px-4 py-2 font-semibold text-brand-brown transition-colors duration-200 hover:bg-brand-beige lg:hidden"
          aria-expanded={open}
          aria-controls="mobile-menu"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
          <span>{open ? 'Schliessen' : 'Menü'}</span>
        </button>
      </div>

      {open && (
        <nav id="mobile-menu" ref={menuRef} aria-label="Mobile Hauptnavigation" className="border-t border-brand-line bg-brand-cream lg:hidden">
          <ul className="container-site flex flex-col py-4">
            {NAV_ITEMS.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `block rounded-sm px-3 py-4 text-lg font-semibold transition-colors duration-200 ${
                      isActive ? 'bg-brand-beige text-brand-olive' : 'text-brand-brown hover:bg-brand-beige'
                    }`
                  }
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </header>
  );
}
